/**
 * Rental Objects Page - Professionelle Mietobjekt-Verwaltung mit Material-UI
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Fab,
  Alert,
  Divider,
  Stack,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  Engineering as EngineeringIcon,
  Public as PublicIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  MoreVert as MoreVertIcon,
  DeleteForever as DeleteForeverIcon,
  ToggleOff as DeactivateIcon,
  ToggleOn as ActivateIcon,
} from "@mui/icons-material";
import { RentalObject, ObjectType } from "../types/api-types";
import { apiClient } from "../services/apiClient";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ConfirmDialog from "../components/Common/ConfirmDialog";

const RentalObjectsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [objects, setObjects] = useState<RentalObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [selectedObject, setSelectedObject] = useState<RentalObject | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPermanentDeleteDialog, setShowPermanentDeleteDialog] =
    useState(false);
  const [objectToDelete, setObjectToDelete] = useState<RentalObject | null>(
    null,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedObjectForMenu, setSelectedObjectForMenu] =
    useState<RentalObject | null>(null);

  // Load objects
  const loadObjects = useCallback(async () => {
    try {
      setLoading(true);
      // Lade aktive oder alle Objekte basierend auf Toggle-Status
      const filters: any = {};
      if (!showInactive) {
        filters.isActive = true;
      } else {
        // includeInactive=true lädt alle Objekte (aktive und deaktivierte)
        filters.includeInactive = true;
      }

      const objectList = await apiClient.getRentalObjects(filters);
      console.log(
        `📦 Loaded rental objects (${showInactive ? "all" : "active only"}):`,
        objectList?.length || 0,
      );
      setObjects(Array.isArray(objectList) ? objectList : []);
    } catch (err: any) {
      console.error("❌ Error loading rental objects:", err);
      setError(err.message || "Fehler beim Laden der Mietobjekte");
      setObjects([]); // Ensure objects is always an array
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadObjects();
  }, [loadObjects]); // loadObjects ist jetzt ein Callback

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownClick = target.closest(".action-menu");

      if (!isDropdownClick) {
        setAnchorEl(null);
        setSelectedObjectForMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Filter objects
  const filteredObjects = (objects || []).filter((object) => {
    const matchesSearch =
      object.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      object.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      object.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      object.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || object.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Handle toggle status (activate/deactivate) object
  const handleToggleStatus = async (object: RentalObject) => {
    try {
      const updatedObject = await apiClient.toggleRentalObjectStatus(object.id);
      await loadObjects();

      console.log(
        `🔄 Object ${updatedObject.isActive ? "activated" : "deactivated"}:`,
        object.name,
      );
    } catch (err: any) {
      console.error("❌ Error toggling object status:", err);
      setError(err.message || "Fehler beim Ändern des Objektstatus");
    }
  };

  // Handle permanent delete
  const handlePermanentDeleteObject = async () => {
    if (!objectToDelete) return;

    try {
      // Permanente Löschung
      await apiClient.permanentDeleteRentalObject(objectToDelete.id);

      // Entferne das Objekt sofort aus der lokalen Liste
      setObjects((prevObjects) => {
        const newObjects = (prevObjects || []).filter(
          (o) => o.id !== objectToDelete.id,
        );
        console.log("💥 Object permanently deleted:", objectToDelete.name);
        console.log("📊 Remaining objects:", newObjects.length);
        return newObjects;
      });

      setShowPermanentDeleteDialog(false);
      setObjectToDelete(null);

      // Optional: Lade die Liste neu um sicherzustellen, dass alles synchron ist
      setTimeout(() => {
        loadObjects();
      }, 100);
    } catch (err: any) {
      console.error("❌ Error permanently deleting object:", err);
      setError(
        err.message || "Fehler beim permanenten Löschen des Mietobjekts",
      );
    }
  };

  // Handle delete (soft delete)
  const handleDeleteObject = async () => {
    if (!objectToDelete) return;

    try {
      // Lösche das Objekt (soft delete im Backend)
      await apiClient.deleteRentalObject(objectToDelete.id);

      setShowDeleteDialog(false);
      setObjectToDelete(null);
      await loadObjects();
    } catch (err: any) {
      console.error("❌ Error deleting object:", err);
      setError(err.message || "Fehler beim Löschen des Mietobjekts");
    }
  };

  // Handle edit
  const handleEditObject = (object: RentalObject) => {
    setSelectedObject(object);
    setShowForm(true);
  };

  // Handle add new
  const handleAddObject = () => {
    setSelectedObject(null);
    setShowForm(true);
  };

  // Handle menu open
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    object: RentalObject,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedObjectForMenu(object);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedObjectForMenu(null);
  };

  // Handle deactivate/activate from menu
  const handleToggleFromMenu = () => {
    if (selectedObjectForMenu) {
      handleToggleStatus(selectedObjectForMenu);
    }
    handleMenuClose();
  };

  // Handle delete from menu
  const handleDeleteFromMenu = () => {
    if (selectedObjectForMenu) {
      setObjectToDelete(selectedObjectForMenu);
      setShowDeleteDialog(true);
    }
    handleMenuClose();
  };

  // Handle permanent delete from menu
  const handlePermanentDeleteFromMenu = () => {
    if (selectedObjectForMenu) {
      setObjectToDelete(selectedObjectForMenu);
      setShowPermanentDeleteDialog(true);
    }
    handleMenuClose();
  };

  // Type display names
  const getTypeDisplayName = (type: string) => {
    const names: Record<string, string> = {
      HEBEBUEHNE: "Hebebühne",
      ARBEITSPLATZ: "Arbeitsplatz",
      Parkplatz: "Parkplatz",
    };
    return names[type] || type;
  };

  // Type icon component
  const getTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactElement> = {
      HEBEBUEHNE: <BuildIcon sx={{ fontSize: "inherit" }} />,
      ARBEITSPLATZ: <EngineeringIcon sx={{ fontSize: "inherit" }} />,
    };
    return icons[type] || <BuildIcon sx={{ fontSize: "inherit" }} />;
  };

  // Get status chip
  const getStatusChip = (object: RentalObject) => {
    if (!object.isActive) {
      return (
        <Chip
          label="Inaktiv"
          color="error"
          size="small"
          icon={<InactiveIcon sx={{ fontSize: "0.9rem !important" }} />}
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.7rem", md: "0.75rem" },
            height: { xs: 22, md: 24 },
            "& .MuiChip-label": {
              px: 1,
            },
            bgcolor: "error.main",
            color: "white",
            border: "1px solid rgba(244, 67, 54, 0.3)",
            boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
          }}
        />
      );
    }
    return (
      <Chip
        label="Aktiv"
        color="success"
        size="small"
        icon={<ActiveIcon sx={{ fontSize: "0.9rem !important" }} />}
        sx={{
          fontWeight: 700,
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          height: { xs: 22, md: 24 },
          "& .MuiChip-label": {
            px: 1,
          },
          bgcolor: "success.main",
          color: "white",
          border: "1px solid rgba(76, 175, 80, 0.3)",
          boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
        }}
      />
    );
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
  };

  if (loading) {
    return <LoadingSpinner text="Lade Mietobjekte..." />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadObjects}>
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (showForm) {
    return (
      <RentalObjectForm
        object={selectedObject}
        onSave={async (objectData) => {
          try {
            if (selectedObject) {
              await apiClient.updateRentalObject(selectedObject.id, objectData);
            } else {
              await apiClient.createRentalObject(objectData);
            }
            await loadObjects();
            setShowForm(false);
            setSelectedObject(null);
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern des Mietobjekts");
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedObject(null);
        }}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 1, md: 3 },
        minHeight: "100vh",
        bgcolor: "grey.50",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: { xs: 2, md: 3 },
          background:
            "linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)",
          color: "white",
          borderRadius: { xs: 2, md: 3 },
          boxShadow: "0 8px 32px rgba(25, 118, 210, 0.15)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 2, sm: 3 }}
        >
          <Box sx={{ zIndex: 1 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 1.5 },
                fontWeight: 700,
                mb: { xs: 0.5, md: 1 },
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <BuildIcon
                fontSize={isMobile ? "medium" : "large"}
                sx={{
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              />
              Mietobjekt-Verwaltung
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.95,
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 400,
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              Verwalten Sie alle Ihre Mietobjekte zentral und effizient
            </Typography>
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={1.5} sx={{ zIndex: 1 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddObject}
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.25)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  },
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  py: 1.2,
                  border: "1px solid rgba(255,255,255,0.2)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  backdropFilter: "blur(10px)",
                }}
              >
                Neues Mietobjekt
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 2,
            mx: { xs: 0.5, md: 0 },
            fontSize: { xs: "0.875rem", md: "1rem" },
            "& .MuiAlert-icon": {
              fontSize: { xs: "1.25rem", md: "1.5rem" },
            },
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 3 },
          mb: { xs: 2, md: 3 },
          borderRadius: { xs: 2, md: 3 },
          mx: { xs: 0.5, md: 0 },
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.06)",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
        }}
      >
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <SearchIcon sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" } }} />
            Filter & Suche
          </Typography>

          <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Mietobjekte durchsuchen"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        color="primary"
                        sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="small"
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "white",
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Name, Beschreibung, Standort, Seriennummer..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: { xs: 2, md: 3 },
                    bgcolor: "white",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    "& fieldset": {
                      borderColor: "rgba(0,0,0,0.12)",
                      borderWidth: "1.5px",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}>
                  Typ Filter
                </InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Typ Filter"
                  sx={{
                    borderRadius: { xs: 2, md: 3 },
                    bgcolor: "white",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0,0,0,0.12)",
                      borderWidth: "1.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderWidth: "2px",
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
                  >
                    <em>Alle Typen</em>
                  </MenuItem>
                  {Object.values(ObjectType).map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      sx={{
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        py: 1,
                        "&:hover": {
                          bgcolor: "primary.light",
                          color: "white",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {getTypeIcon(type)}
                        {getTypeDisplayName(type)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    color="primary"
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: { xs: "1.2rem", md: "1.4rem" },
                      },
                    }}
                  />
                }
                label="Deaktivierte anzeigen"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                    fontWeight: 500,
                    color: "text.primary",
                  },
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                  border: "1px solid rgba(25, 118, 210, 0.2)",
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Stack
                direction={{ xs: "row", sm: "row" }}
                spacing={1}
                justifyContent={{ xs: "center", md: "flex-end" }}
                alignItems="center"
              >
                {(searchTerm || typeFilter) && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon fontSize="small" />}
                    onClick={clearFilters}
                    sx={{
                      borderRadius: { xs: 2, md: 3 },
                      fontSize: { xs: "0.8rem", md: "0.9rem" },
                      px: { xs: 2, md: 2.5 },
                      py: { xs: 0.8, md: 1 },
                      borderColor: "grey.400",
                      color: "grey.700",
                      "&:hover": {
                        borderColor: "error.main",
                        bgcolor: "error.light",
                        color: "white",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isMobile ? "Reset" : "Filter zurücksetzen"}
                  </Button>
                )}
                {isMobile && (
                  <Button
                    variant="contained"
                    fullWidth={!searchTerm && !typeFilter}
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddObject}
                    sx={{
                      borderRadius: { xs: 2, md: 3 },
                      fontSize: { xs: "0.85rem", md: "0.9rem" },
                      px: { xs: 2.5, md: 3 },
                      py: { xs: 1, md: 1.2 },
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Neues Objekt
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Objects Display */}
      {filteredObjects.length === 0 ? (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: "center",
            borderRadius: { xs: 3, md: 4 },
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            border: "1px solid rgba(0,0,0,0.08)",
            mx: { xs: 0.5, md: 0 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 50% 50%, rgba(25, 118, 210, 0.05) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <BuildIcon
              sx={{
                fontSize: { xs: 48, md: 72 },
                color: "primary.main",
                opacity: 0.7,
                mb: { xs: 2, md: 3 },
                filter: "drop-shadow(0 4px 8px rgba(25, 118, 210, 0.2))",
              }}
            />
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{
                fontWeight: 700,
                color: "grey.800",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                mb: { xs: 1.5, md: 2 },
              }}
            >
              {searchTerm || typeFilter
                ? "Keine passenden Objekte gefunden"
                : "Noch keine Mietobjekte vorhanden"}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              gutterBottom
              sx={{
                fontSize: { xs: "0.9rem", md: "1rem" },
                lineHeight: 1.6,
                maxWidth: "500px",
                mx: "auto",
                mb: { xs: 2.5, md: 3 },
              }}
            >
              {searchTerm || typeFilter
                ? "Versuchen Sie andere Suchbegriffe oder erstellen Sie ein neues Mietobjekt für Ihren Bestand."
                : "Beginnen Sie mit der Erfassung Ihres ersten Mietobjekts und bauen Sie Ihren digitalen Gerätepark auf."}
            </Typography>
            {(!searchTerm && !typeFilter) || isMobile ? (
              <Button
                variant="contained"
                size={isMobile ? "medium" : "large"}
                startIcon={<AddIcon />}
                onClick={handleAddObject}
                sx={{
                  mt: { xs: 1, md: 2 },
                  borderRadius: { xs: 2, md: 3 },
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.2, md: 1.5 },
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  fontWeight: 600,
                  boxShadow: "0 6px 20px rgba(25, 118, 210, 0.3)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(25, 118, 210, 0.4)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {searchTerm || typeFilter
                  ? "Neues Objekt anlegen"
                  : "Erstes Mietobjekt anlegen"}
              </Button>
            ) : (
              <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  Filter zurücksetzen
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddObject}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  Neues Objekt
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      ) : (
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          sx={{ px: { xs: 0.5, md: 0 } }}
        >
          {filteredObjects.map((object) => (
            <Grid item xs={12} sm={6} lg={4} key={object.id}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: { xs: 2, md: 3 },
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  "&:hover": {
                    transform: { xs: "none", md: "translateY(-4px)" },
                    boxShadow: {
                      xs: "0 4px 16px rgba(0,0,0,0.12)",
                      md: "0 8px 32px rgba(0,0,0,0.15)",
                    },
                    borderColor: "primary.light",
                  },
                  opacity: !object.isActive ? 0.65 : 1,
                  bgcolor: !object.isActive ? "grey.50" : "white",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": !object.isActive
                    ? {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          "linear-gradient(90deg, #f44336 0%, #ff9800 100%)",
                        zIndex: 1,
                      }
                    : {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background:
                          "linear-gradient(90deg, #4caf50 0%, #2196f3 100%)",
                        zIndex: 1,
                      },
                }}
                onClick={() => handleEditObject(object)}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 3 },
                    pt: { xs: 2.5, md: 3.5 },
                  }}
                >
                  {/* Header with Status */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: { xs: 1.5, md: 2 },
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "primary.light",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {getTypeIcon(object.type)}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1rem", md: "1.125rem" },
                          textDecoration: !object.isActive
                            ? "line-through"
                            : "none",
                          color: !object.isActive
                            ? "text.secondary"
                            : "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.3,
                        }}
                        title={object.name}
                      >
                        {object.name}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>{getStatusChip(object)}</Box>
                  </Box>

                  {/* Type and Description */}
                  <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
                    <Chip
                      label={getTypeDisplayName(object.type)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                        height: { xs: 24, md: 28 },
                        fontWeight: 600,
                        borderWidth: 1.5,
                        mb: object.description ? 1.5 : 0,
                      }}
                    />
                    {object.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.5,
                          fontSize: { xs: "0.8rem", md: "0.875rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          mt: 1,
                        }}
                      >
                        {object.description}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: { xs: 1.5, md: 2 } }} />

                  {/* Details Grid */}
                  <Grid
                    container
                    spacing={{ xs: 1, md: 1.5 }}
                    sx={{ mb: { xs: 1.5, md: 2 } }}
                  >
                    {object.location && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            bgcolor: "grey.50",
                            p: 1,
                            borderRadius: 1,
                          }}
                        >
                          <LocationIcon
                            fontSize="small"
                            color="primary"
                            sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}
                          />
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                              fontWeight: 500,
                            }}
                          >
                            {object.location}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {[
                      { label: "Seriennr.", value: object.serialNumber },
                      { label: "Hersteller", value: object.manufacturer },
                      { label: "Modell", value: object.model },
                      { label: "Baujahr", value: object.year },
                      {
                        label: "Max. Gewicht",
                        value: object.maxCapacity
                          ? `${object.maxCapacity} kg`
                          : null,
                      },
                    ]
                      .filter((item) => item.value)
                      .map((item, index) => (
                        <Grid item xs={6} key={index}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.8rem" },
                              lineHeight: 1.4,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ fontWeight: 600, color: "text.primary" }}
                            >
                              {item.label}:
                            </Box>
                            <br />
                            {item.value}
                          </Typography>
                        </Grid>
                      ))}

                    {object.basePrice && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            bgcolor: "primary.light",
                            color: "white",
                            p: 1.5,
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: "1rem", md: "1.125rem" },
                            }}
                          >
                            {formatCurrency(object.basePrice)}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                ml: 0.5,
                                opacity: 0.9,
                                fontSize: { xs: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              /
                              {object.rentalUnit === "MINUTES"
                                ? "Min"
                                : object.rentalUnit === "HOURS"
                                  ? "Std"
                                  : "Tag"}
                            </Typography>
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {/* Special Features */}
                  {(object.isPublic || object.requiresTraining) && (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ gap: 0.5 }}
                    >
                      {object.isPublic && (
                        <Chip
                          label="Öffentlich"
                          size="small"
                          icon={
                            <PublicIcon
                              sx={{ fontSize: "0.9rem !important" }}
                            />
                          }
                          color="info"
                          variant="outlined"
                          sx={{
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                            height: { xs: 22, md: 24 },
                          }}
                        />
                      )}
                      {object.requiresTraining && (
                        <Chip
                          label="Einweisung erforderlich"
                          size="small"
                          icon={
                            <EngineeringIcon
                              sx={{ fontSize: "0.9rem !important" }}
                            />
                          }
                          color="warning"
                          variant="outlined"
                          sx={{
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                            height: { xs: 22, md: 24 },
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </CardContent>

                <CardActions
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    pt: 0,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.25",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditObject(object);
                      }}
                      sx={{
                        flex: 1,
                        fontSize: { xs: "0.75rem", md: "0.8rem" },
                        py: { xs: 0.5, md: 0.75 },
                        borderRadius: 1.5,
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "white",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      Bearbeiten
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      color={object.isActive ? "warning" : "success"}
                      startIcon={
                        object.isActive ? (
                          <DeactivateIcon fontSize="small" />
                        ) : (
                          <ActivateIcon fontSize="small" />
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(object);
                      }}
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                        py: { xs: 0.5, md: 0.75 },
                        px: { xs: 1, md: 1.5 },
                        borderRadius: 1.5,
                        "&:hover": {
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isMobile
                        ? object.isActive
                          ? "Aus"
                          : "An"
                        : object.isActive
                          ? "Deaktivieren"
                          : "Aktivieren"}
                    </Button>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, object);
                      }}
                      className="action-menu"
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1.5,
                        p: { xs: 0.5, md: 0.75 },
                        "&:hover": {
                          borderColor: "primary.main",
                          backgroundColor: "primary.light",
                          color: "white",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: { xs: 20, md: 24 },
          right: { xs: 20, md: 24 },
          display: { xs: "flex", md: "none" },
          width: { xs: 56, md: 64 },
          height: { xs: 56, md: 64 },
          boxShadow: "0 8px 24px rgba(25, 118, 210, 0.4)",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: "0 12px 32px rgba(25, 118, 210, 0.5)",
          },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1000,
        }}
        onClick={handleAddObject}
      >
        <AddIcon sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" } }} />
      </Fab>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            minWidth: { xs: 180, md: 220 },
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.08)",
            mt: 0.5,
            "& .MuiMenuItem-root": {
              fontSize: { xs: "0.85rem", md: "0.9rem" },
              py: 1.5,
              px: 2,
              borderRadius: 1,
              mx: 0.5,
              my: 0.25,
              "&:hover": {
                borderRadius: 1,
              },
            },
          },
        }}
      >
        {selectedObjectForMenu && (
          <>
            <MenuItem
              onClick={handleToggleFromMenu}
              sx={{
                color: selectedObjectForMenu.isActive
                  ? "warning.main"
                  : "success.main",
                "&:hover": {
                  bgcolor: selectedObjectForMenu.isActive
                    ? "warning.light"
                    : "success.light",
                  color: "white",
                },
              }}
            >
              <ListItemIcon>
                {selectedObjectForMenu.isActive ? (
                  <DeactivateIcon fontSize="small" sx={{ color: "inherit" }} />
                ) : (
                  <ActivateIcon fontSize="small" sx={{ color: "inherit" }} />
                )}
              </ListItemIcon>
              <ListItemText>
                {selectedObjectForMenu.isActive ? "Deaktivieren" : "Aktivieren"}
              </ListItemText>
            </MenuItem>

            <MenuItem
              onClick={handleDeleteFromMenu}
              sx={{
                color: "warning.main",
                "&:hover": {
                  bgcolor: "warning.light",
                  color: "white",
                },
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: "inherit" }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2">Soft Delete</Typography>
                <Typography variant="caption" color="text.secondary">
                  (wiederherstellbar)
                </Typography>
              </ListItemText>
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={handlePermanentDeleteFromMenu}
              sx={{
                color: "error.main",
                "&:hover": {
                  bgcolor: "error.light",
                  color: "white",
                },
              }}
            >
              <ListItemIcon>
                <DeleteForeverIcon fontSize="small" sx={{ color: "inherit" }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  ⚠️ Permanent löschen
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (unwiderruflich!)
                </Typography>
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Mietobjekt deaktivieren (Soft Delete)"
        message={`Möchten Sie das Mietobjekt "${objectToDelete?.name}" deaktivieren? 

Das Objekt wird aus der normalen Ansicht ausgeblendet, bleibt aber in der Datenbank gespeichert und kann jederzeit wiederhergestellt werden.

Diese Aktion kann rückgängig gemacht werden.`}
        confirmText="Deaktivieren"
        cancelText="Abbrechen"
        type="warning"
        onConfirm={handleDeleteObject}
        onCancel={() => {
          setShowDeleteDialog(false);
          setObjectToDelete(null);
        }}
      />

      <ConfirmDialog
        isOpen={showPermanentDeleteDialog}
        title="⚠️ PERMANENT LÖSCHEN"
        message={`ACHTUNG: Diese Aktion kann NICHT rückgängig gemacht werden!

Das Mietobjekt "${objectToDelete?.name}" wird permanent aus der Datenbank gelöscht.

Alle zugehörigen Daten gehen unwiderruflich verloren, einschließlich:
• Miethistorie
• Preisstrukturen
• Zugeordnete Artikel
• Alle anderen Verknüpfungen

Sind Sie sich absolut sicher?`}
        confirmText="JA, PERMANENT LÖSCHEN"
        cancelText="Abbrechen"
        type="danger"
        onConfirm={handlePermanentDeleteObject}
        onCancel={() => {
          setShowPermanentDeleteDialog(false);
          setObjectToDelete(null);
        }}
      />
    </Container>
  );
};

// Rental Object Form Component
interface RentalObjectFormProps {
  object: RentalObject | null;
  onSave: (object: any) => void;
  onCancel: () => void;
}

const RentalObjectForm: React.FC<RentalObjectFormProps> = ({
  object,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    name: object?.name || "",
    description: object?.description || "",
    type: object?.type || "HEBEBUEHNE",
    location: object?.location || "",
    serialNumber: object?.serialNumber || "",
    manufacturer: object?.manufacturer || "",
    model: object?.model || "",
    year: object?.year?.toString() || "",
    maxCapacity: object?.maxCapacity?.toString() || "",
    requiresTraining: object?.requiresTraining || false,
    isPublic: object?.isPublic || false,
    isActive: object?.isActive !== undefined ? object.isActive : true,
    // Neue Mieteinheit-Felder
    rentalUnit: object?.rentalUnit || "HOURS",
    minimumRentalDuration: object?.minimumRentalDuration?.toString() || "1",
    rentalInterval: object?.rentalInterval?.toString() || "1",
    basePrice: object?.basePrice?.toString() || "10.0",
    specifications: object?.specifications
      ? JSON.stringify(object.specifications, null, 2)
      : "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let specifications = {};
    try {
      if (formData.specifications.trim()) {
        specifications = JSON.parse(formData.specifications);
      }
    } catch (err) {
      alert("Ungültiges JSON-Format in den Spezifikationen");
      return;
    }

    const objectData = {
      ...formData,
      year: formData.year ? parseInt(formData.year) : undefined,
      maxCapacity: formData.maxCapacity
        ? parseFloat(formData.maxCapacity)
        : undefined,
      // Neue Mieteinheit-Felder konvertieren
      minimumRentalDuration: parseFloat(formData.minimumRentalDuration),
      rentalInterval: parseFloat(formData.rentalInterval),
      basePrice: parseFloat(formData.basePrice),
      specifications,
    };

    onSave(objectData);
  };

  // Type display names
  const getTypeDisplayName = (type: string) => {
    const names: Record<string, string> = {
      HEBEBUEHNE: "Hebebühne",
      ARBEITSPLATZ: "Arbeitsplatz",
    };
    return names[type] || type;
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <BuildIcon fontSize={isMobile ? "medium" : "large"} />
              {object ? "Mietobjekt bearbeiten" : "Neues Mietobjekt anlegen"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {object
                ? "Bearbeiten Sie die Details des Mietobjekts"
                : "Erstellen Sie ein neues Mietobjekt für die Vermietung"}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Grundinformationen
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Objektname"
                    variant="outlined"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel>Typ *</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      label="Typ *"
                      required
                    >
                      {Object.values(ObjectType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {getTypeDisplayName(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Detaillierte Beschreibung des Mietobjekts"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Location & Identification */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Standort & Identifikation
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Standort"
                    variant="outlined"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    placeholder="z.B. Halle A, Platz 1"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Seriennummer"
                    variant="outlined"
                    value={formData.serialNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, serialNumber: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Hersteller"
                    variant="outlined"
                    value={formData.manufacturer}
                    onChange={(e) =>
                      setFormData({ ...formData, manufacturer: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Modell"
                    variant="outlined"
                    value={formData.model}
                    onChange={(e) =>
                      setFormData({ ...formData, model: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Baujahr"
                    variant="outlined"
                    type="number"
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({ ...formData, year: e.target.value })
                    }
                    inputProps={{
                      min: "1900",
                      max: "2030",
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Technical Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Technische Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max. Gewicht (kg)"
                    variant="outlined"
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) =>
                      setFormData({ ...formData, maxCapacity: e.target.value })
                    }
                    inputProps={{
                      min: "0",
                      step: "0.1",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Mieteinheit</InputLabel>
                    <Select
                      value={formData.rentalUnit}
                      onChange={(e) =>
                        setFormData({ ...formData, rentalUnit: e.target.value })
                      }
                      label="Mieteinheit"
                    >
                      <MenuItem value="MINUTES">Minuten</MenuItem>
                      <MenuItem value="HOURS">Stunden</MenuItem>
                      <MenuItem value="DAYS">Tage</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={`Mindestmietdauer (${
                      formData.rentalUnit === "MINUTES"
                        ? "Min"
                        : formData.rentalUnit === "HOURS"
                          ? "Std"
                          : "Tage"
                    })`}
                    variant="outlined"
                    type="number"
                    value={formData.minimumRentalDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        minimumRentalDuration: e.target.value,
                      })
                    }
                    placeholder="z.B. 30 für 30 Minuten"
                    inputProps={{
                      min: "0.1",
                      step: "0.01",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={`Abrechnungsintervall (${
                      formData.rentalUnit === "MINUTES"
                        ? "Min"
                        : formData.rentalUnit === "HOURS"
                          ? "Std"
                          : "Tage"
                    })`}
                    variant="outlined"
                    type="number"
                    value={formData.rentalInterval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        rentalInterval: e.target.value,
                      })
                    }
                    placeholder="z.B. 15 für 15-Minuten-Takt"
                    inputProps={{
                      min: "0.1",
                      step: "0.01",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={`Grundpreis (€/${
                      formData.rentalUnit === "MINUTES"
                        ? "Minute"
                        : formData.rentalUnit === "HOURS"
                          ? "Stunde"
                          : "Tag"
                    })`}
                    variant="outlined"
                    type="number"
                    value={formData.basePrice}
                    onChange={(e) =>
                      setFormData({ ...formData, basePrice: e.target.value })
                    }
                    placeholder="Grundpreis ohne Bundle-Aufschläge"
                    inputProps={{
                      min: "0.01",
                      step: "0.01",
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Settings & Specifications */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Einstellungen & Spezifikationen
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.requiresTraining}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            requiresTraining: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Einweisung erforderlich"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isPublic}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isPublic: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Öffentlich sichtbar"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isActive: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Aktiv"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Spezifikationen (JSON)"
                    variant="outlined"
                    multiline
                    rows={6}
                    value={formData.specifications}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        specifications: e.target.value,
                      })
                    }
                    placeholder='{"eigenschaft": "wert", "maximale_hoehe": 1900, "leistung": "3,2 kW"}'
                    helperText="JSON-Format für technische Daten, z.B. Abmessungen, Leistung, etc."
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  onClick={onCancel}
                  sx={{ borderRadius: 2 }}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ borderRadius: 2, px: 4 }}
                >
                  {object ? "Speichern" : "Erstellen"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default RentalObjectsPage;
